import type { Logger } from "@expert/logging";
import type { Partner } from "@expert/shared-types";

import { isAbortLikeError, withMetrics } from "@expert/cloudwatch-metrics";
import { type GaiaWebSocketResponse, type GaiaWebsocketContext, gaiaWsEventBus } from "@expert/gaia";

import type { SpellCheckResponse } from "@/sdk/agent/chat/types";

import { userCache } from "@/shared-utils";

import { SPELLCHECK_CONFIG } from "./config";

export type { SpellCheckResponse, SpellCheckState, SpellCheckStatus } from "@/sdk/agent/chat/types";

/** The app name GAIA expects for the orchestrator-invoke request. */
const SPELLCHECK_APP_NAME = "expert-assist-messaging-spell-check";

/** Request payload GAIA expects for the orchestrator-invoke request. */
interface GaiaSpellCheckRequest {
    partner: Partner;
    sessionId: string;
    chatId: string;
    message: string;
}

interface GaiaSpellCheckResponseBody {
    rewriting_needed: boolean;
    rewritten_text: string;
    /** Array of the original text snippets that were corrected by GAIA */
    highlight_snippets: string[];
}

export interface GaiaSpellCheckOptions {
    /** Override API timeout (ms). Default: SPELLCHECK_CONFIG.apiTimeoutMs */
    timeoutMs?: number;
    /** AbortSignal for cancelling the request */
    signal?: AbortSignal;
}

/**
 * Parse spellcheck data from a GAIA orchestrator-invoke-success response body.
 * The response field is a JSON string containing the actual spellcheck result.
 * @internal Exported for unit testing
 */
export function parseSpellCheckBody(body: unknown, logger: Logger): GaiaSpellCheckResponseBody | null {
    const payload = body as Record<string, unknown>;
    const responseStr = payload?.response as string | undefined;

    if (!responseStr) {
        logger.warn({ body }, "GAIA response missing 'response' field");
        return null;
    }

    try {
        return JSON.parse(responseStr) as GaiaSpellCheckResponseBody;
    } catch (parseError) {
        logger.warn({ body, parseError }, "Failed to parse GAIA response JSON string");
        return null;
    }
}

/**
 * Send a spellcheck request via the GAIA WebSocket and wait for the response
 * on the event bus, matched by correlationId using the persistent WebSocket connection.
 *
 * Caller must ensure request.sessionId, request.chatId, and a non-empty (after trim) message are provided.
 */
export const gaiaSpellCheck = async (
    request: GaiaSpellCheckRequest,
    sendJsonMessage: GaiaWebsocketContext["sendJsonMessage"],
    logger: Logger,
    options?: GaiaSpellCheckOptions,
): Promise<SpellCheckResponse | undefined> => {
    const timeoutMs = options?.timeoutMs ?? SPELLCHECK_CONFIG.apiTimeoutMs;

    const correlationId = crypto.randomUUID();

    const res = await withMetrics(
        "gaiaSpellCheck",
        () =>
            new Promise<GaiaSpellCheckResponseBody>((resolve, reject) => {
                let cleanedUp = false;

                const cleanup = () => {
                    if (cleanedUp) return;
                    cleanedUp = true;
                    unsubSuccess();
                    unsubError();
                    clearTimeout(timer);
                };

                const unsubSuccess = gaiaWsEventBus.on(
                    "gaia_ws_orchestrator-invoke-success",
                    (response: GaiaWebSocketResponse) => {
                        if (response.correlationId !== correlationId) return;

                        cleanup();
                        const parsed = parseSpellCheckBody(response.body, logger);
                        if (parsed) {
                            resolve(parsed);
                        } else {
                            reject(new Error("Failed to parse spellcheck response"));
                        }
                    },
                );

                const unsubError = gaiaWsEventBus.on(
                    "gaia_ws_orchestrator-invoke-error",
                    (response: GaiaWebSocketResponse) => {
                        if (response.correlationId !== correlationId) return;

                        cleanup();
                        logger.warn({ body: response.body, correlationId }, "Spellcheck orchestrator error");
                        reject(new Error("Spellcheck orchestrator error"));
                    },
                );

                const timer = setTimeout(() => {
                    cleanup();
                    reject(new Error(`Spellcheck timed out after ${timeoutMs}ms`));
                }, timeoutMs);

                const signal = options?.signal;
                if (signal) {
                    const rejectAborted = () => {
                        cleanup();
                        reject(
                            signal.reason != null
                                ? signal.reason
                                : new DOMException("The operation was aborted.", "AbortError"),
                        );
                    };
                    // Pre-abort listener check in case the signal is aborted before the request is sent
                    if (signal.aborted) {
                        rejectAborted();
                        return;
                    }
                    signal.addEventListener("abort", rejectAborted, { once: true });
                }

                sendJsonMessage({
                    action: "orchestrator-invoke",
                    appName: SPELLCHECK_APP_NAME,
                    correlationId,
                    messageId: crypto.randomUUID(),
                    message: request.message,
                    sessionId: request.chatId,
                    sessionGroupId: request.sessionId,
                    userId: userCache.employeeId,
                    userType: "assistant",
                    partner: request.partner,
                });
            }),
        {
            logger: logger.child(request),
            rethrow: true,
            logResult: false,
            shouldCountAsCancelled: isAbortLikeError,
        },
    );

    if (!res) {
        logger.warn({ request }, "Spellcheck returned no result");
        return undefined;
    }

    if (
        res.rewriting_needed === undefined ||
        res.rewritten_text === undefined ||
        res.highlight_snippets === undefined
    ) {
        // TODO: switch to zod validation
        logger.warn({ request, response: res }, "Spellcheck returned invalid response structure");
        return undefined;
    }

    return {
        rewritingNeeded: res.rewriting_needed,
        rewrittenText: res.rewritten_text,
        highlightSnippets: res.highlight_snippets,
    };
};
